import { useCallback, useEffect, useState } from 'react';
import { bffQuery } from '../lib/bffClient';

/**
 * One performer's free times from the slot ENGINE (PM-0468 §2.4,
 * `scheduling.ListBookableSlots` through the clinic BFF's `bookableSlots`):
 * `days` clinic-local days from `fromDate`, every slot free or taken with a
 * reason, anchored at each shift's start on the clinic's clock, in the
 * service's real length. The desk reads with MARK (an OUTSIDE_SERVICE_WINDOW
 * hour comes back marked, not hidden) and START_INSIDE.
 *
 * `durationMinutes` is the caller's resolved length — the engine never guesses
 * one, so nothing is asked until the caller has it. The server stays the
 * judge: a `free: true` cell is a proposal the write re-validates.
 */
export const BOOKABLE_SLOTS = `
  query BookableSlots($serviceCatalogId: ID, $providerType: String!, $providerId: ID!, $fromDate: String!, $days: Int, $durationMinutes: Int!, $fit: String) {
    bookableSlots(serviceCatalogId: $serviceCatalogId, providerType: $providerType, providerId: $providerId, fromDate: $fromDate, days: $days, durationMinutes: $durationMinutes, fit: $fit) {
      date
      slots { startMs free reason }
    }
  }
`;

/** The wire's provider type: the BFF reads `doctor` | `staff`. */
export const providerTypeOf = (performer) => (String(performer?.type || 'DOCTOR').toUpperCase() === 'STAFF' ? 'staff' : 'doctor');

export async function fetchBookableSlots({
  serviceCatalogId = null, performer, fromDate, days = 14, durationMinutes, fit = 'START_INSIDE',
}) {
  const d = await bffQuery(BOOKABLE_SLOTS, {
    serviceCatalogId: serviceCatalogId || null,
    providerType: providerTypeOf(performer),
    providerId: performer.id,
    fromDate,
    days,
    durationMinutes,
    fit,
  }, 'BookableSlots');
  return d?.bookableSlots || [];
}

export function useBookableSlots({
  enabled = true, serviceCatalogId = null, performer = null, fromDate, days = 14, durationMinutes = null,
  fit = 'START_INSIDE', refreshKey = 0,
} = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const ready = !!enabled && !!performer?.id && !!fromDate && Number(durationMinutes) > 0;
  const performerKey = performer ? `${performer.type}:${performer.id}` : '';

  const load = useCallback(async () => {
    if (!ready) { setData([]); setError(null); setLoading(false); return; }
    setLoading(true);
    try {
      const rows = await fetchBookableSlots({ serviceCatalogId, performer, fromDate, days, durationMinutes, fit });
      setData(rows);
      setError(null);
    } catch (e) {
      setData([]);
      setError(e?.message || 'bookableSlots');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, serviceCatalogId, performerKey, fromDate, days, durationMinutes, fit, refreshKey]);

  useEffect(() => { load(); }, [load]);

  return { days: data, loading, error, reload: load, ready };
}

export default useBookableSlots;
